import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { TrialBalancePageView } from './TrialBalancePageView';
import { useTrialBalancePage } from './useTrialBalancePage';

/**
 * `/accounting/statements/trial-balance` — the trial balance.
 *
 * Owns its data hook and an `ErrorBoundary`; the throw lives in the inner
 * Content child because React boundaries don't catch errors thrown by the
 * component that renders them (the repo's self-served page pattern).
 *
 * The boundary is doing real work here, not ceremony. With the ledger
 * unreachable the only alternatives are a spinner that never ends or a table of
 * zeros that balances perfectly — and the second is the most convincing wrong
 * answer this product can give. A failed read has no figures to render, so it
 * throws, and the fallback offers a retry.
 */
export const TrialBalancePage: React.FC = () => {
    const page = useTrialBalancePage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('trialBalance.error.title'),
            errorMessage: t('trialBalance.error.message'),
            errorRetry: t('trialBalance.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('trial-balance-page')}
        >
            <ErrorBoundary
                data-testid="trial-balance-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="trial-balance-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <TrialBalancePageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type TrialBalancePageContentProps = {
    page: ReturnType<typeof useTrialBalancePage>;
};

const TrialBalancePageContent: React.FC<TrialBalancePageContentProps> = ({
    page,
}) => {
    if (page.error) {
        throw page.error;
    }

    // The View keeps its remaining control and Print while loading and skeletons
    // only the totals and the rows — the repo's loading-state standard.
    return (
        <TrialBalancePageView
            asOfText={page.asOfText}
            closingNoteKey={page.closingNoteKey}
            closingView={page.closingView}
            generatedText={page.generatedText}
            isEmpty={page.isEmpty}
            isLoading={page.isLoading || page.rows === null}
            onClosingViewChange={page.onClosingViewChange}
            onViewLedger={page.onViewLedger}
            rows={page.rows ?? []}
            totals={page.totals}
        />
    );
};
